import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CallPlannerTab } from 'features/Yucollect/AgencyMonitor/types/AllocationListDataType';
import { Agent } from 'features/Yucollect/CallPlanner/types/CallPlannerDataType';
import { Column, GroupItem } from 'features/Yucollect/utils/types';
import {
  CallDataGridRecord,
  IBorrowerStatsRequest,
  IBorrowerStatsResponse,
  ICallDataGridSummary,
  ICallPlannerMetricsRequest,
  ICallPlannerMetricsResponse,
  ICreateBorrowerGroupsRequest,
  ICreateBorrowerGroupsResponse,
  IFailedPayload,
  IFetchAgentsByCampaignRequest,
  IFetchAgentsByCampaignResponse,
  IFetchAgentsInAndOutOfCampaignRequest,
  IFetchBorrowerGroupsRequest,
  IFetchBorrowerGroupsResponse,
  IFetchBorrowersInGroupRequest,
  IFetchBorrowersInGroupResponse,
  IFetchCampaignAllocationSummaryRequest,
  IFetchCampaignAllocationSummaryResponse,
  IFetchCampaignsRequest,
  IFetchCampaignsResponse,
  IGetCallDataGridHeadersRequest,
  IGetCallDataGridHeadersResponse,
  IGetCallDataGridRequest,
  IGetCallDataGridResponse,
  IGetCallDataGridSummaryRequest,
  IGetCallDataGridSummaryResponse,
  IGetCallUniqueValuesByColumnRequest,
  IGetCallUniqueValuesByColumnResponse,
  IGetSearchInDataGridRequest,
  IGetSearchInDataGridResponse,
  IHeaders,
  IReassignCampaignBorrowerGroupRequest,
  ISaveLastSelectedGroupRequest,
  ISaveLastSelectedGroupResponse,
  ISearchInFilterByValuesRequest,
  ISearchInFilterByValuesResponse,
  StandardPagination,
} from 'redux/types/Call.types';

export interface CallPlannerState {
  data: CallDataGridRecord[];
  headers: IHeaders[];
  dataGridLoader: boolean;
  headersLoader: boolean;
  summaryLoader: boolean;
  downloadExcelFileLoader: boolean;
  uniqueValuesLoader: boolean;
  searchLoader: boolean;
  error: IFailedPayload | null;
  uniqueValues: string[];
  filteredResultsByValues: string[];
  bucket: string | null;
  channel: string | null;
  pagination: StandardPagination;
  allocationId: string;
  allocationName: string;
  allocationStartDate: string | null;
  allocationEndDate: string | null;
  invalid_records: number;
  no_follow_up_record: number;
  total_records: number;
  filters: Column[];
  summary: ICallDataGridSummary;
  callPlannerMetrics: ICallPlannerMetricsResponse;
  totalNumberOfRecords: number;
  loading: boolean;
  unassignLoader: boolean;
  reassignLoader: boolean;
  campaigns: IFetchCampaignsResponse[];
  assignedAgents: Agent[];
  otherAgents: Agent[];
  borrowerGroups: IFetchBorrowerGroupsResponse[];
  borrowerGroupsLoading: boolean;
  campaignAllocationSummary: IFetchCampaignAllocationSummaryResponse;
  message: string;
  campaignWiseAgents: IFetchAgentsByCampaignResponse[];
  unassignCampaignBorrowerGroupMessage: string;
  reassignCampaignBorrowerGroupMessage: string;
  selectedGroup: GroupItem | null;
  totalRecords: number;
  activeCallPlannerTab: string;
  borrowerStats: Record<string, IBorrowerStatsResponse>;
  borrowerStatsLoading: Record<string, boolean>;
}

export const callPlannerInitState: CallPlannerState = {
  data: [],
  uniqueValues: [],
  filteredResultsByValues: [],
  dataGridLoader: false,
  headersLoader: false,
  uniqueValuesLoader: false,
  summaryLoader: false,
  downloadExcelFileLoader: false,
  searchLoader: false,
  error: null,
  bucket: null,
  channel: null,
  pagination: {
    limit: 0,
    offset: 0,
    page: 0,
    totalCount: 0,
    pageSize: 0,
  },
  allocationId: '',
  allocationName: '',
  allocationStartDate: null,
  allocationEndDate: null,
  invalid_records: 0,
  no_follow_up_record: 0,
  total_records: 0,
  headers: [],
  filters: [],
  summary: {
    sumWeightedAverage: '0',
    avgOutstanding: '0',
    sumOutstanding: '0',
    numberOfRecords: '0',
  },
  callPlannerMetrics: {
    'Total Call Allocation Count': {
      sumOutstanding: 0,
      numberOfRecords: 0,
      sumWeightedAverage: 0,
    },
    'Total Field Allocation Count': {
      sumOutstanding: 0,
      numberOfRecords: 0,
      sumWeightedAverage: 0,
    },
    'Unallocated Count': {
      sumOutstanding: 0,
      numberOfRecords: 0,
      sumWeightedAverage: 0,
    },
    'Capacity Utilization': '',
    'Unused Capacity': 0,
    'Resolution %': 0,
  },
  totalNumberOfRecords: 0,
  loading: false,
  unassignLoader: false,
  reassignLoader: false,
  campaigns: [],
  assignedAgents: [],
  otherAgents: [],
  borrowerGroups: [],
  borrowerGroupsLoading: false,
  campaignAllocationSummary: {
    data: [],
    pagination: {
      limit: 0,
      offset: 0,
      page: 0,
      totalCount: 0,
      pageSize: 0,
    },
  },
  message: '',
  campaignWiseAgents: [],
  unassignCampaignBorrowerGroupMessage: '',
  reassignCampaignBorrowerGroupMessage: '',
  selectedGroup: null,
  totalRecords: 0,
  activeCallPlannerTab: CallPlannerTab.BORROWER_GROUPS,
  borrowerStats: {},
  borrowerStatsLoading: {},
};

const callPlannerSlice = createSlice({
  name: 'callPlanner',
  initialState: callPlannerInitState,
  reducers: {
    fetchCallPlannerDataGrid: (
      state,
      action: PayloadAction<IGetCallDataGridRequest>
    ) => {
      state.dataGridLoader = true;
      state.error = null;
    },
    fetchCallPlannerDataGridSuccess: (
      state,
      action: PayloadAction<IGetCallDataGridResponse>
    ) => {
      state.data = action.payload.data;
      state.allocationId = action.payload.allocation_id;
      state.allocationName = action.payload.allocation_file_name;
      state.pagination = action.payload.pagination;
      state.dataGridLoader = false;
      state.totalRecords = action.payload.totalRecords;
      state.allocationStartDate = action.payload.allocation_start_date;
      state.allocationEndDate = action.payload.allocation_end_date;
    },
    fetchCallPlannerDataGridFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.dataGridLoader = false;
    },
    // Headers
    fetchCallPlannerDataGridHeaders: (
      state,
      action: PayloadAction<IGetCallDataGridHeadersRequest>
    ) => {
      state.headersLoader = true;
      state.error = null;
    },
    fetchCallPlannerDataGridHeadersSuccess: (
      state,
      action: PayloadAction<IGetCallDataGridHeadersResponse>
    ) => {
      state.headers = action.payload;
      state.headersLoader = false;
    },
    fetchCallPlannerDataGridHeadersFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.headersLoader = false;
    },
    // Summary
    fetchCallPlannerDataGridSummary: (
      state,
      action: PayloadAction<IGetCallDataGridSummaryRequest>
    ) => {
      state.summaryLoader = true;
      state.error = null;
    },
    fetchCallPlannerDataGridSummarySuccess: (
      state,
      action: PayloadAction<IGetCallDataGridSummaryResponse>
    ) => {
      state.summary = action.payload;
      state.summaryLoader = false;
    },
    fetchCallPlannerDataGridSummaryFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.summaryLoader = false;
    },

    // Unique values by column
    getCallPlannerDataGridUniqueValuesByColumnName: (
      state,
      action: PayloadAction<IGetCallUniqueValuesByColumnRequest>
    ) => {
      state.uniqueValuesLoader = true;
      state.error = null;
    },
    getCallPlannerDataGridUniqueValuesByColumnNameSuccess: (
      state,
      action: PayloadAction<IGetCallUniqueValuesByColumnResponse>
    ) => {
      state.uniqueValues = action.payload;
      state.uniqueValuesLoader = false;
    },
    getCallPlannerDataGridUniqueValuesByColumnNameFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.uniqueValuesLoader = false;
    },
    resetUniqueValues: (state) => {
      state.uniqueValuesLoader = false;
      state.error = null;
      state.uniqueValues = [];
    },

    // Search in filter by values
    searchInFilterByValues: (
      state,
      action: PayloadAction<ISearchInFilterByValuesRequest>
    ) => {
      state.searchLoader = true;
      state.error = null;
    },
    searchInFilterByValuesSuccess: (
      state,
      action: PayloadAction<ISearchInFilterByValuesResponse>
    ) => {
      // state.filteredResultsByValues = action.payload;
      state.uniqueValues = action.payload;
      state.searchLoader = false;
    },
    searchInFilterByValuesFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.searchLoader = false;
    },

    // Global search
    searchInDataGrid: (
      state,
      action: PayloadAction<IGetSearchInDataGridRequest>
    ) => {
      state.dataGridLoader = true;
      state.error = null;
    },
    searchInDataGridSuccess: (
      state,
      action: PayloadAction<IGetSearchInDataGridResponse>
    ) => {
      state.data = action.payload.data;
      state.allocationId = action.payload.allocation_id;
      state.allocationName = action.payload.allocation_file_name;
      state.pagination = action.payload.pagination;
      state.dataGridLoader = false;
    },
    searchInDataGridFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.dataGridLoader = false;
    },
    // Call planner metrics
    fetchCallPlannerMetrics: (
      state,
      action: PayloadAction<ICallPlannerMetricsRequest>
    ) => {
      state.summaryLoader = true;
      state.error = null;
    },
    fetchCallPlannerMetricsSuccess: (
      state,
      action: PayloadAction<ICallPlannerMetricsResponse>
    ) => {
      state.callPlannerMetrics = action.payload;
      state.summaryLoader = false;
    },
    fetchCallPlannerMetricsFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.summaryLoader = false;
    },
    // fetch campaigns
    fetchCampaigns: (state, action: PayloadAction<IFetchCampaignsRequest>) => {
      state.loading = true;
      state.error = null;
    },
    fetchCampaignsSuccess: (
      state,
      action: PayloadAction<IFetchCampaignsResponse[]>
    ) => {
      state.campaigns = action.payload;
      state.loading = false;
    },
    fetchCampaignsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },
    // // fetch campaigns
    // fetchAgentsInAndOutOfCampaign: (
    //   state,
    //   action: PayloadAction<IFetchAgentsInAndOutOfCampaignRequest>
    // ) => {
    //   state.loading = true;
    //   state.error = null;
    // },
    // fetchAgentsInAndOutOfCampaignSuccess: (
    //   state,
    //   action: PayloadAction<{
    //     assignedAgents: Agent[];
    //     otherAgents: Agent[];
    //   }>
    // ) => {
    //   state.assignedAgents = action.payload.assignedAgents;
    //   state.otherAgents = action.payload.otherAgents;
    //   state.loading = false;
    // },
    // fetchAgentsInAndOutOfCampaignFailure: (
    //   state,
    //   action: PayloadAction<IFailedPayload>
    // ) => {
    //   state.error = action.payload;
    //   state.loading = false;
    // },

    // Assigned Agents
    fetchAssignedAgents: (
      state,
      action: PayloadAction<IFetchAgentsInAndOutOfCampaignRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchAssignedAgentsSuccess: (state, action: PayloadAction<Agent[]>) => {
      state.assignedAgents = action.payload;
      state.loading = false;
    },

    // Other Agents
    fetchOtherAgents: (
      state,
      action: PayloadAction<IFetchAgentsInAndOutOfCampaignRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchOtherAgentsSuccess: (state, action: PayloadAction<Agent[]>) => {
      state.otherAgents = action.payload;
      state.loading = false;
    },

    // Common failure action
    fetchAgentsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.loading = false;
    },

    // create borrower group
    createBorrowerGroup: (
      state,
      action: PayloadAction<ICreateBorrowerGroupsRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    createBorrowerGroupSuccess: (
      state,
      action: PayloadAction<ICreateBorrowerGroupsResponse>
    ) => {
      // state.borrowerGroups = action.payload;
      state.loading = false;
      state.message = action.payload.message;
    },
    createBorrowerGroupFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // fetch borrower groups by supervisor
    fetchBorrowerGroupsBySupervisorId: (
      state,
      action: PayloadAction<IFetchBorrowerGroupsRequest>
    ) => {
      state.borrowerGroupsLoading = true;
      state.error = null;
    },
    fetchBorrowerGroupsBySupervisorIdSuccess: (
      state,
      action: PayloadAction<IFetchBorrowerGroupsResponse[]>
    ) => {
      state.borrowerGroups = action.payload;
      state.borrowerGroupsLoading = false;
    },
    fetchBorrowerGroupsBySupervisorIdFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.borrowerGroupsLoading = false;
    },
    // fetch borrowers in group by group id
    fetchBorrowersInGroup: (
      state,
      action: PayloadAction<IFetchBorrowersInGroupRequest>
    ) => {
      state.dataGridLoader = true;
      state.error = null;
    },
    fetchBorrowersInGroupSuccess: (
      state,
      action: PayloadAction<IFetchBorrowersInGroupResponse>
    ) => {
      state.data = action.payload.data;
      state.allocationId = action.payload.allocation_id;
      state.allocationName = action.payload.allocation_file_name;
      state.pagination = action.payload.pagination;
      state.dataGridLoader = false;
    },
    fetchBorrowersInGroupFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.dataGridLoader = false;
    },
    // fetch borrowers in group by group id
    fetchCampaignAllocationSummary: (
      state,
      action: PayloadAction<IFetchCampaignAllocationSummaryRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchCampaignAllocationSummarySuccess: (
      state,
      action: PayloadAction<IFetchCampaignAllocationSummaryResponse>
    ) => {
      state.campaignAllocationSummary = action.payload;
      state.dataGridLoader = false;
    },
    fetchCampaignAllocationSummaryFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    // Fetch agents by campaign
    fetchAgentsByCampaign: (
      state,
      action: PayloadAction<IFetchAgentsByCampaignRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchAgentsByCampaignSuccess: (
      state,
      action: PayloadAction<IFetchAgentsByCampaignResponse[]>
    ) => {
      state.campaignWiseAgents = action.payload;
      state.loading = false;
    },
    fetchAgentsByCampaignFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },
    // unassigning campaign from borrower group
    unassignCampaignBorrowerGroup: (
      state,
      action: PayloadAction<{ groupId: string; allocationId: string }>
    ) => {
      state.unassignLoader = true;
      state.error = null;
    },
    unassignCampaignBorrowerGroupSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      state.unassignLoader = false;
      state.unassignCampaignBorrowerGroupMessage = action.payload.message;
    },
    unassignCampaignBorrowerGroupFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.unassignLoader = false;
    },

    // reassigning campaign from borrower group
    reassignCampaignBorrowerGroup: (
      state,
      action: PayloadAction<IReassignCampaignBorrowerGroupRequest>
    ) => {
      state.reassignLoader = true;
      state.error = null;
    },
    reassignCampaignBorrowerGroupSuccess: (
      state,
      action: PayloadAction<{ message: string }>
    ) => {
      state.reassignLoader = false;
      state.reassignCampaignBorrowerGroupMessage = action.payload.message;
    },
    reassignCampaignBorrowerGroupFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.reassignLoader = false;
    },
    // save last selected group
    saveLastSelectedGroup: (
      state,
      action: PayloadAction<ISaveLastSelectedGroupRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    saveLastSelectedGroupSuccess: (
      state,
      action: PayloadAction<ISaveLastSelectedGroupResponse>
    ) => {
      state.loading = false;
    },
    saveLastSelectedGroupFailure: (
      state,
      action: PayloadAction<IFailedPayload>
    ) => {
      state.error = action.payload;
      state.loading = false;
    },

    resetCallCenterAllocationSummary: (state) => {
      state.data = [];
      state.callPlannerMetrics = {
        'Total Call Allocation Count': {
          sumOutstanding: 0,
          numberOfRecords: 0,
          sumWeightedAverage: 0,
        },
        'Total Field Allocation Count': {
          sumOutstanding: 0,
          numberOfRecords: 0,
          sumWeightedAverage: 0,
        },
        'Unallocated Count': {
          sumOutstanding: 0,
          numberOfRecords: 0,
          sumWeightedAverage: 0,
        },
        'Capacity Utilization': '',
        'Unused Capacity': 0,
        'Resolution %': 0,
      };
    },
    resetCallFieldMessages: (state) => {
      state.message = '';
      state.reassignCampaignBorrowerGroupMessage = '';
      state.unassignCampaignBorrowerGroupMessage = '';
    },
    setSelectedGroup: (state, action: PayloadAction<GroupItem | null>) => {
      if (action.payload) {
        const selectedGroupId = action.payload.id;
        state.borrowerGroups = state.borrowerGroups.map((group) => ({
          ...group,
          isGroupSelected: group.id === selectedGroupId,
        }));

        state.selectedGroup = {
          ...action.payload,
          isGroupSelected: true,
        };
      } else {
        state.selectedGroup = action.payload;
      }
    },
    setActiveCallPlannerTab: (state, action: PayloadAction<string>) => {
      state.activeCallPlannerTab = action.payload;
    },

    fetchBorrowerStats: (
      state,
      action: PayloadAction<IBorrowerStatsRequest>
    ) => {
      state.borrowerStatsLoading[action.payload.campaignId] = true;
      state.error = null;
    },
    fetchBorrowerStatsSuccess: (
      state,
      action: PayloadAction<{
        campaignId: string;
        data: IBorrowerStatsResponse;
      }>
    ) => {
      state.borrowerStats[action.payload.campaignId] = action.payload.data;
      state.borrowerStatsLoading[action.payload.campaignId] = false;
    },
    fetchBorrowerStatsFailure: (
      state,
      action: PayloadAction<{ error: string; campaignId: string }>
    ) => {
      state.error = { error: { message: action.payload.error } };
      state.borrowerStatsLoading[action.payload.campaignId] = false;
    },
  },
});

export const {
  fetchCallPlannerDataGrid,
  fetchCallPlannerDataGridSuccess,
  fetchCallPlannerDataGridFailure,
  fetchCallPlannerDataGridHeaders,
  fetchCallPlannerDataGridHeadersSuccess,
  fetchCallPlannerDataGridHeadersFailure,
  fetchCallPlannerDataGridSummary,
  fetchCallPlannerDataGridSummarySuccess,
  fetchCallPlannerDataGridSummaryFailure,
  getCallPlannerDataGridUniqueValuesByColumnName,
  getCallPlannerDataGridUniqueValuesByColumnNameSuccess,
  getCallPlannerDataGridUniqueValuesByColumnNameFailure,
  resetUniqueValues,
  searchInFilterByValues,
  searchInFilterByValuesSuccess,
  searchInFilterByValuesFailure,
  searchInDataGrid,
  searchInDataGridSuccess,
  searchInDataGridFailure,
  fetchCallPlannerMetrics,
  fetchCallPlannerMetricsSuccess,
  fetchCallPlannerMetricsFailure,
  fetchCampaigns,
  fetchCampaignsSuccess,
  fetchCampaignsFailure,
  fetchBorrowerGroupsBySupervisorId,
  fetchBorrowerGroupsBySupervisorIdSuccess,
  fetchBorrowerGroupsBySupervisorIdFailure,
  fetchBorrowersInGroup,
  fetchBorrowersInGroupSuccess,
  fetchBorrowersInGroupFailure,
  fetchCampaignAllocationSummary,
  fetchCampaignAllocationSummarySuccess,
  fetchCampaignAllocationSummaryFailure,
  createBorrowerGroup,
  createBorrowerGroupSuccess,
  createBorrowerGroupFailure,
  fetchAgentsByCampaign,
  fetchAgentsByCampaignSuccess,
  fetchAgentsByCampaignFailure,
  fetchAssignedAgents,
  fetchAssignedAgentsSuccess,
  fetchOtherAgents,
  fetchOtherAgentsSuccess,
  fetchAgentsFailure,
  resetCallCenterAllocationSummary,
  unassignCampaignBorrowerGroup,
  unassignCampaignBorrowerGroupSuccess,
  unassignCampaignBorrowerGroupFailure,
  reassignCampaignBorrowerGroup,
  reassignCampaignBorrowerGroupSuccess,
  reassignCampaignBorrowerGroupFailure,
  saveLastSelectedGroup,
  saveLastSelectedGroupSuccess,
  saveLastSelectedGroupFailure,
  resetCallFieldMessages,
  setSelectedGroup,
  setActiveCallPlannerTab,
  fetchBorrowerStats,
  fetchBorrowerStatsSuccess,
  fetchBorrowerStatsFailure,
} = callPlannerSlice.actions;

export default callPlannerSlice.reducer;
